import React, { useEffect, useRef } from 'react';
import FieldRenderer from './FieldRenderer';

const JobFormSection = ({
  title,
  fields,
  formConfig,
  jobData,
  inputClass,
  workerOptions,
  teamOptions,
  partnerOptions,
  sparePartsOptions,
  serviceLocation,
  onChange,
  onQuickAddSparePart,
  onQuickAddCategory,
  onQuickAddService,
  onQuickAddArrivalMethod,
  suggestions,
  showSuggestions,
  onSuggestionSelect,
  variant = 'card',
  isOpen = true,
  onToggle = () => {},
  onOpenDeviceImages,
  onOpenDeviceQr,
  lockAssignmentFields = false
}) => {
  // Map fields to their row positions across all sections
  // Row 0: clientName, deviceType, serviceDateTime_date, serviceType, warranty
  // Row 1: clientPhone, deviceBrand, serviceDateTime_time, estimatedDuration
  // Row 2: serviceLocation, deviceModel, assignedTo_worker, priority
  // Row 3: clientAddress_street, deviceSerialNumber, assignedTo_team
  // Row 4: assignedTo_partner, notes
  // Row 4: clientAddress_parts
  // Row 5: clientEmail, issueDescription
  const fieldRowMap = {
    // Row 0: clientName, deviceType, serviceDateTime_date, serviceType, warranty
    'clientName': 0,
    'deviceType': 0,
    'serviceDateTime_date': 0,
    'serviceType': 0,
    'warranty': 0,
    // Row 1: clientPhone, deviceBrand, serviceDateTime_time, estimatedDuration
    'clientPhone': 1,
    'deviceBrand': 1,
    'serviceDateTime_time': 1,
    'estimatedDuration': 1,
    // Row 2: serviceLocation, deviceModel, assignedTo_worker, priority
    'serviceLocation': 2,
    'serviceArrivalMethod': 3,
    'deviceModel': 2,
    'assignedTo_worker': 2,
    'priority': 2,
    // Row 3: clientAddress_street, deviceSerialNumber, assignedTo_team, serviceArrivalMethod
    'clientAddress_street': 3,
    'deviceSerialNumber': 3,
    'deviceImagesTrigger': 3,
    'assignedTo_team': 3,
    // Row 4: assignedTo_partner, notes
    'assignedTo_partner': 4,
    'notes': 4,
    // Row 4: clientAddress_parts
    'clientAddress_parts': 4,
    // Row 5: clientEmail, issueDescription
    'clientEmail': 5,
    'issueDescription': 5,
    // Company/legal block in client section
    'isBusinessClient': 6,
    'businessClient_companyName': 7,
    'businessClient_contactPerson': 8,
    'businessClient_pib': 9,
    'businessClient_registrationNumber': 10,
    'businessClient_address': 11
  };
  const fieldsContainerRef = useRef(null);

  const visibleFields = fields.filter((fieldName) => {
    const config = formConfig[fieldName];
    // Allow custom trigger field without config
    if (!config && fieldName !== 'deviceImagesTrigger') return false;
    if (fieldName === 'clientAddress') {
      if (serviceLocation !== 'OnSite') return false;
    }
    if (fieldName === 'serviceArrivalMethod') {
      if (serviceLocation !== 'InWorkshop') return false;
    }
    if (fieldName.startsWith('businessClient_')) {
      if (!jobData?.isBusinessClient) return false;
    }
    return true;
  });

  useEffect(() => {
    if (!fieldsContainerRef.current) return;
    
    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      // Find all field wrappers in this section
      const fieldWrappers = fieldsContainerRef.current.querySelectorAll('[data-field-row]');
      if (fieldWrappers.length === 0) return;

      // Group fields by their row index
      const rows = new Map();
      fieldWrappers.forEach((wrapper) => {
        const rowIndex = parseInt(wrapper.getAttribute('data-field-row'));
        if (!rows.has(rowIndex)) {
          rows.set(rowIndex, []);
        }
        rows.get(rowIndex).push(wrapper);
      });

      // Calculate max height for each row and apply it
      rows.forEach((wrappers) => {
        let maxHeight = 0;
        wrappers.forEach((wrapper) => {
          const height = wrapper.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        });
        
        // Limit max height to prevent excessive spacing (max 50px)
        const limitedHeight = Math.min(maxHeight, 50);
        
        // Apply max height to all wrappers in this row
        wrappers.forEach((wrapper) => {
          wrapper.style.minHeight = `${limitedHeight}px`;
        });
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [visibleFields.length, jobData, serviceLocation]);

  if (visibleFields.length === 0) return null;

  const renderFields = () => (
    <div ref={fieldsContainerRef} className="grid grid-cols-1 gap-1">
      {visibleFields.map((fieldName, index) => {
        let fieldValue;
        // Handle address parts
        if (fieldName === 'clientAddress' || fieldName === 'clientAddress_street' || fieldName === 'clientAddress_parts') {
          fieldValue = jobData.clientAddress;
        } 
        // Handle serviceDateTime parts
        else if (fieldName === 'serviceDateTime' || fieldName === 'serviceDateTime_time' || fieldName === 'serviceDateTime_date') {
          fieldValue = jobData.serviceDateTime;
        } 
        // Handle assignedTo parts
        else if (
          fieldName === 'assignedTo' ||
          fieldName === 'assignedTo_worker' ||
          fieldName === 'assignedTo_team' ||
          fieldName === 'assignedTo_partner'
        ) {
          fieldValue = jobData.assignedTo;
        }
        // Handle notes
        else if (fieldName === 'notes') {
          fieldValue = jobData.notes;
        }
        // Handle deviceType
        else if (fieldName === 'deviceType') {
          fieldValue = jobData.deviceCategoryId || jobData.deviceTypeId || jobData.deviceType || '';
        }
        // Handle serviceArrivalMethod
        else if (fieldName === 'serviceArrivalMethod') {
          fieldValue = jobData.serviceArrivalMethodId || jobData.serviceArrivalMethodName || '';
        }
        // Handle business client nested object fields
        else if (fieldName.startsWith('businessClient_')) {
          fieldValue = jobData.businessClient || {};
        }
        // Handle serviceId
        else if (fieldName === 'serviceId') {
          fieldValue = jobData.serviceId || '';
        }
        // Default
        else {
          fieldValue = jobData[fieldName];
        }
        
        if (fieldName === 'clientPhone') {
          console.log('🔍 JobFormSection - Prosleđujem value za clientPhone:', {
            fieldName,
            fieldValue,
            jobDataClientPhone: jobData.clientPhone,
            areEqual: fieldValue === jobData.clientPhone
          });
        }
        
        // Row number. When InWorkshop use compact rows (30, 31) for serviceArrivalMethod and clientEmail so they don't get stretched by cross-section alignment (uklanja prazne blokove između načina dolaska i emaila).
        let rowNumber = fieldRowMap[fieldName] !== undefined ? fieldRowMap[fieldName] : index;
        if (serviceLocation === 'InWorkshop') {
          if (fieldName === 'serviceArrivalMethod') rowNumber = 30;
          else if (fieldName === 'clientEmail') rowNumber = 31;
        }
        
        // Custom field: deviceImages trigger – renders button inside section
        if (fieldName === 'deviceImagesTrigger') {
          return (
            <div key={fieldName} data-field-row={rowNumber} className="flex items-center justify-end gap-2 px-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onOpenDeviceImages}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                >
                  Slike aparata
                </button>
                <button
                  type="button"
                  onClick={onOpenDeviceQr}
                  className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs rounded"
                >
                  QR aparata
                </button>
              </div>
            </div>
          );
        }

        return (
          <div key={fieldName} data-field-row={rowNumber}>
            <FieldRenderer
              fieldName={fieldName}
              fieldConfig={formConfig[fieldName]}
              value={fieldValue}
              onChange={onChange}
              inputClass={inputClass}
              workerOptions={workerOptions}
              teamOptions={teamOptions}
              partnerOptions={partnerOptions}
              sparePartsOptions={sparePartsOptions}
              serviceLocation={serviceLocation}
              onQuickAddSparePart={onQuickAddSparePart}
              onQuickAddCategory={onQuickAddCategory}
              onQuickAddService={onQuickAddService}
              onQuickAddArrivalMethod={onQuickAddArrivalMethod}
              jobData={jobData}
              suggestions={suggestions?.[fieldName] || []}
              showSuggestions={showSuggestions?.[fieldName] || false}
              onSuggestionSelect={onSuggestionSelect ? (client) => onSuggestionSelect(client, fieldName) : undefined}
              lockAssignmentFields={lockAssignmentFields}
            />
          </div>
        );
      })}
    </div>
  );

  if (variant === 'accordion') {
    return (
      <div className="rounded-md border border-gray-700 bg-gray-900">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between px-3 py-2 text-left"
        >
          <span className="text-sm font-medium text-gray-100">{title}</span>
          <svg
            className={`h-4 w-4 text-gray-400 transition-transform transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="border-t border-gray-800 px-3 py-3">
            {renderFields()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-800 p-4">
      <h3 className="mb-4 text-lg font-medium text-white">{title}</h3>
      {renderFields()}
    </div>
  );
};

export default JobFormSection;


